import colormap from "colormap";
import { rgbStringToHex } from "utils/color/rgbHelpers";

import { IColorPalette } from "components/user-settings/models";

export type BinDatum = { label: string; min: number; max: number; count: number };

/**
 * Maps each numeric bin's label to its palette color.
 * Includes regular numeric bins plus open-ended numeric bins where only one
 * bound is defined (e.g. less-than / greater-than bins), and skips bins with
 * no numeric bounds at all (e.g. n/a) so the color scale matches the backend.
 */
export function generateBinColors(
  palette: IColorPalette,
  binData: BinDatum[]
): Map<string, string> {
  const numericBins = binData.filter(
    (bin) => bin.min !== undefined || bin.max !== undefined
  );

  const colors = generateColors(palette, numericBins.length);
  const colorMap = new Map<string, string>();

  numericBins.forEach((bin, index) => {
    colorMap.set(bin.label, colors[index]);
  });

  return colorMap;
}

/**
 * Generates color array from palette
 */
export function generateColors(palette: IColorPalette, count: number): string[] {
  let colors: string[] = [];
  try {
    colors = colormap({
      colormap: palette?.name,
      nshades: count,
      format: "hex",
      alpha: 1
    });
  } catch {
    colors = mapColors(palette?.colors, count, palette?.reverse);
  }
  if (palette?.reverse === true) {
    colors = colors.reverse();
  }
  return colors;
}

// Map colors to the size of the unique attributes, spread the color scale
export function mapColors(
  paletteColors: string[],
  size: number,
  reverse: boolean
): string[] {
  const colors: string[] = [];
  const colorsToUse = reverse ? [...paletteColors].reverse() : paletteColors;

  if (size === 1) {
    colors.push(rgbStringToHex(paletteColors[0]));
    return colors;
  }

  const step = (colorsToUse.length - 1) / (size - 1);
  for (let i = 0; i < size; i++) {
    const index = Math.round(i * step);
    colors.push(rgbStringToHex(paletteColors[index]));
  }
  return colors;
}
